import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const ClientUploadModal = ({
  showModal,
  previewImage,
  handleImageChange,
  handleSubmit,
  closeModal,
  submitting,
  message,
  error,
}) => {
  if (!showModal) return null;

  return createPortal(
    <div style={styles.modalOverlay} onClick={closeModal}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Add Client Image</h3>

          <button type="button" onClick={closeModal} style={styles.closeBtn}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.imageBox}>
            <label style={styles.label}>Client Image</label>

            {previewImage ? (
              <img
                src={previewImage}
                alt="Client Preview"
                style={styles.image}
              />
            ) : (
              <div style={styles.noImageBox}>No image selected</div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={styles.input}
              required
            />
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.button} disabled={submitting}>
            {submitting ? "Uploading..." : "Upload Client Image"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

const ClientsEditor = () => {
  const [clients, setClients] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const getClientImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    return `https://eventscons.com/eventbackendnew/storage/app/public/${imagePath}`;
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch("https://eventscons.com/eventbackendnew/api/clients");

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      const clientData = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
        ? result.data
        : [];

      setClients(clientData);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedImage(null);
    setPreviewImage("");
    setMessage("");
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedImage(null);
    setPreviewImage("");
    setMessage("");
    setError("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);
      setSelectedImage(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");
    setError("");

    try {
      if (!selectedImage) {
        throw new Error("Please select an image");
      }

      const payload = new FormData();
      payload.append("image", selectedImage);

      const response = await fetch("https://eventscons.com/eventbackendnew/api/clients", {
        method: "POST",
        body: payload,
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Client image upload failed");
      }

      setMessage(result.message || "Client image uploaded successfully");
      await fetchClients();

      setTimeout(() => {
        closeModal();
      }, 800);
    } catch (err) {
      setError(err.message || "Something went wrong while uploading");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this client image?"
    );

    if (!confirmDelete) return;

    try {
      setDeletingId(id);
      setMessage("");
      setError("");

      const response = await fetch(
        `https://eventscons.com/eventbackendnew/api/clients/${id}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Delete failed");
      }

      setMessage(result.message || "Client image deleted successfully");
      await fetchClients();
    } catch (err) {
      setError(err.message || "Something went wrong while deleting");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p style={styles.loading}>Loading client images...</p>;
  if (error && clients.length === 0) return <p style={styles.error}>{error}</p>;

  return (
    <>
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.headerRow}>
            <div>
              <h2 style={styles.heading}>Clients</h2>
              <p style={styles.subHeading}>
                Manage client logos/images with upload and delete actions.
              </p>
            </div>

            <button
              type="button"
              style={styles.addButton}
              onClick={openAddModal}
            >
              + Add Client Image
            </button>
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          {clients.length === 0 ? (
            <div style={styles.emptyBox}>
              <p style={styles.emptyText}>No client images found.</p>
              <button
                type="button"
                style={styles.addButton}
                onClick={openAddModal}
              >
                + Upload First Client Image
              </button>
            </div>
          ) : (
            <div style={styles.grid}>
              {clients.map((client) => (
                <div key={client.id} style={styles.card}>
                  <div style={styles.cardImageWrap}>
                    <img
                      src={getClientImageUrl(client.image)}
                      alt={`Client ${client.id}`}
                      style={styles.cardImage}
                    />
                  </div>

                  <div style={styles.cardBody}>
                    {/* <p style={styles.cardId}>Client ID: {client.id}</p> */}

                    <button
                      type="button"
                      style={{
                        ...styles.deleteBtn,
                        opacity: deletingId === client.id ? 0.7 : 1,
                      }}
                      onClick={() => handleDelete(client.id)}
                      disabled={deletingId === client.id}
                    >
                      {deletingId === client.id
                        ? "Deleting..."
                        : "Delete Client"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <ClientUploadModal
        showModal={showModal}
        previewImage={previewImage}
        handleImageChange={handleImageChange}
        handleSubmit={handleSubmit}
        closeModal={closeModal}
        submitting={submitting}
        message={message}
        error={error}
      />
    </>
  );
};

const styles = {
  section: {
    padding: "40px 20px",
    border: "1px solid #eee",
    background: "#fff",
    margin: "30px",
    boxShadow: "0px 10px 25px #dcdcdc",
    borderRadius: "15px",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    marginBottom: "25px",
    flexWrap: "wrap",
  },
  heading: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#000",
    textAlign: "left",
  },
  subHeading: {
    margin: "8px 0 0 0",
    color: "#6b7280",
    fontSize: "15px",
    fontWeight: "500",
  },
  addButton: {
    padding: "12px 18px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "20px",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
    border: "1px solid #efefef",
  },
  cardImageWrap: {
    padding: "18px",
    background: "#fafafa",
    borderBottom: "1px solid #efefef",
  },
  cardImage: {
    width: "100%",
    height: "140px",
    objectFit: "contain",
    display: "block",
    background: "#fff",
    borderRadius: "10px",
  },
  cardBody: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "flex-start",
  },
  cardId: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "600",
    color: "#444",
  },
  deleteBtn: {
    padding: "12px 16px",
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  emptyBox: {
    padding: "40px 20px",
    textAlign: "center",
    border: "1px dashed #d1d5db",
    borderRadius: "14px",
    background: "#fafafa",
  },
  emptyText: {
    margin: "0 0 16px 0",
    color: "#6b7280",
    fontWeight: "500",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 99999,
  },
  modal: {
    position: "relative",
    width: "100%",
    maxWidth: "700px",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  modalTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#222",
    margin: 0,
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "30px",
    cursor: "pointer",
    color: "#444",
    lineHeight: 1,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  imageBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  image: {
    width: "100%",
    maxWidth: "100%",
    height: "260px",
    objectFit: "contain",
    borderRadius: "10px",
    border: "1px solid #ddd",
    background: "#fff",
  },
  noImageBox: {
    height: "180px",
    border: "1px dashed #bbb",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    background: "#fafafa",
    fontWeight: "500",
  },
  label: {
    fontWeight: "600",
    color: "#333",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    width: "100%",
    boxSizing: "border-box",
  },
  button: {
    padding: "14px 20px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  success: {
    color: "green",
    fontWeight: "600",
    marginBottom: "16px",
  },
  error: {
    color: "red",
    fontWeight: "600",
    marginBottom: "16px",
  },
  loading: {
    padding: "40px",
    textAlign: "center",
  },
};

export default ClientsEditor;